/**
 * GPU ripple simulation: ping-pong RG16F height/velocity field (RGBA8 packed fallback), stepped with the
 * Evan Wallace wave stencil; drops are splatted in the same pass. Pure splat math lives in ripple.ts.
 */
import { bindTextureUnit, canRenderTo, createProgram, createTarget, destroyTarget, type FullscreenQuad, type Program, type Target, type TargetOptions } from './gl';
import { DropQueue, MAX_DROPS, WAVE } from './ripple';
import { QUAD_VERT } from './shaders/quad';
import { RIPPLE_FRAG } from './shaders/ripple';

export class RippleSim {
  readonly packed: boolean;
  readonly size: number;
  private readonly gl: WebGL2RenderingContext;
  private readonly quad: FullscreenQuad;
  private readonly prog: Program;
  private targets: [Target, Target];
  private cur = 0;
  private readonly texel: [number, number];

  constructor(gl: WebGL2RenderingContext, quad: FullscreenQuad, size: number) {
    this.gl = gl;
    this.quad = quad;
    this.size = size;
    const floatOpts: TargetOptions = { internalFormat: gl.RG16F, format: gl.RG, type: gl.HALF_FLOAT, filter: gl.LINEAR };
    const floatOk = !!gl.getExtension('EXT_color_buffer_float') || !!gl.getExtension('EXT_color_buffer_half_float');
    this.packed = !(floatOk && canRenderTo(gl, floatOpts));
    const opts: TargetOptions = this.packed
      ? { internalFormat: gl.RGBA8, format: gl.RGBA, type: gl.UNSIGNED_BYTE, filter: gl.LINEAR }
      : floatOpts;
    this.targets = [createTarget(gl, size, size, opts), createTarget(gl, size, size, opts)];
    this.prog = createProgram(gl, QUAD_VERT, RIPPLE_FRAG, this.packed ? ['PACKED'] : []);
    this.texel = [1 / size, 1 / size];
    this.clear();
  }

  /** current height texture (read by the main pass) */
  get texture(): WebGLTexture { return this.targets[this.cur]!.textures[0]!; }

  clear(): void {
    const gl = this.gl;
    for (const t of this.targets) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, t.fb);
      // packed rest state is (0.5, 0, 0.5, 0) = zero height / velocity; float rest state is 0
      if (this.packed) gl.clearColor(0.5, 0, 0.5, 0); else gl.clearColor(0, 0, 0, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  /** One stencil step; `drops` are consumed (cleared) by the call. */
  step(drops: DropQueue): void {
    const gl = this.gl;
    const src = this.targets[this.cur]!;
    const dst = this.targets[1 - this.cur]!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, dst.fb);
    gl.viewport(0, 0, this.size, this.size);
    this.prog.use();
    bindTextureUnit(gl, 0, src.textures[0]!, this.prog.loc('uPrev'));
    gl.uniform2f(this.prog.loc('uTexel'), this.texel[0], this.texel[1]);
    gl.uniform1f(this.prog.loc('uDamp'), WAVE.damping);
    const n = Math.min(drops.count, MAX_DROPS);
    gl.uniform1i(this.prog.loc('uDropCount'), n);
    if (n > 0) gl.uniform4fv(this.prog.loc('uDrops'), drops.data, 0, n * 4);
    this.quad.draw();
    this.cur = 1 - this.cur;
    drops.clear();
  }

  destroy(): void {
    destroyTarget(this.gl, this.targets[0]);
    destroyTarget(this.gl, this.targets[1]);
    this.prog.destroy();
  }
}
